import { z } from 'zod';
import {
  CONTENT_STATUSES,
  FULFILMENT_METHODS,
  LEAD_SOURCES,
  LEAD_STATUSES,
  MANUAL_MOVEMENT_TYPES,
  ORDER_STATUSES,
  PAYMENT_METHODS,
  PRODUCT_STATUSES,
} from '../constants.js';
import {
  emailSchema,
  linkSchema,
  optionalText,
  phoneSchema,
  priceSchema,
  requiredText,
  seoSchema,
  slugSchema,
  sortOrderSchema,
} from './common.js';

/**
 * The store — catalogue, stock, orders and enquiries.
 *
 * Two rules run through all of it, and both are in TECHNICAL_DOCUMENT.md §6:
 *
 * 1. **Quantities are integers.** This business sells discrete units of hardware.
 *    Nothing accepts a fractional quantity, anywhere, so no rounding rule is needed
 *    and none can be got wrong.
 * 2. **Prices are decimal strings.** See `money.ts`. A price never becomes a float
 *    between the form and the database.
 */

/** Quantity — a whole number of units. The ceiling is a typo guard, not a stock limit. */
export const quantitySchema = z.coerce.number().int().min(1).max(100_000);

/* -------------------------------------------------------------------------- */
/* Taxonomy                                                                    */
/* -------------------------------------------------------------------------- */

export const categorySchema = z
  .object({
    slug: slugSchema,
    name: requiredText(120, 'Name'),
    description: optionalText(1000),
    imageUrl: optionalText(500),
    /** One level of nesting is supported; the API refuses a parent that has a parent. */
    parentId: z.coerce.number().int().positive().optional().nullable(),
    isActive: z.boolean().default(true),
    sortOrder: sortOrderSchema,
    ...seoSchema.shape,
  })
  .strict();
export type CategoryInput = z.infer<typeof categorySchema>;

export const brandSchema = z
  .object({
    slug: slugSchema,
    name: requiredText(120, 'Name'),
    description: optionalText(1000),
    logoUrl: optionalText(500),
    websiteUrl: linkSchema.optional().nullable(),
    isActive: z.boolean().default(true),
    sortOrder: sortOrderSchema,
  })
  .strict();
export type BrandInput = z.infer<typeof brandSchema>;

/* -------------------------------------------------------------------------- */
/* Products                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * SKU — the code on the shelf and the paperwork.
 *
 * Uppercased on the way in so `eps-t82` and `EPS-T82` cannot both exist. It is unique,
 * and it is the one identifier a human reads off a box and types into a search field.
 */
export const skuSchema = z
  .string()
  .trim()
  .toUpperCase()
  .min(2, 'A SKU is required')
  .max(60)
  .regex(/^[A-Z0-9][A-Z0-9._\-/]*$/, 'Use letters, numbers, dot, hyphen, underscore or slash');

export const productSchema = z
  .object({
    sku: skuSchema,
    slug: slugSchema,
    name: requiredText(200, 'Name'),
    brandId: z.coerce.number().int().positive().optional().nullable(),
    categoryId: z.coerce.number().int().positive().optional().nullable(),
    shortDescription: optionalText(400),
    description: optionalText(40_000),
    price: priceSchema,
    /**
     * The "was" price struck through beside the current one. The API refuses a value
     * at or below `price` — a strike-through that is not a saving is a lie told by a
     * form nobody meant to tell, and it is the sort of thing a consumer regulator
     * takes an interest in.
     */
    compareAtPrice: priceSchema.optional().nullable(),
    currency: z.string().trim().toUpperCase().length(3).default('LKR'),
    /** Model number as printed by the manufacturer. Shown under the title. */
    model: optionalText(120),
    /** Free-form spec rows rendered as the Technical Specifications table. */
    specs: z
      .array(z.object({ label: requiredText(120, 'Label'), value: requiredText(400, 'Value') }))
      .max(60)
      .default([]),
    highlights: z.array(requiredText(300, 'Highlight')).max(12).default([]),
    warrantyText: optionalText(400),
    weightGrams: z.coerce.number().int().min(0).max(10_000_000).optional().nullable(),
    status: z.enum(PRODUCT_STATUSES).default('DRAFT'),
    isFeatured: z.boolean().default(false),
    /**
     * Sell past zero. Off by default: the shop shows real availability, and an order
     * the warehouse cannot fill is worse than a lost sale. On for items ordered in on
     * demand, where the lead time is the honest answer rather than "out of stock".
     */
    allowBackorder: z.boolean().default(false),
    sortOrder: sortOrderSchema,
    ...seoSchema.shape,
  })
  .strict()
  .refine(
    (p) => p.compareAtPrice == null || Number(p.compareAtPrice) > Number(p.price),
    { message: 'The compare-at price must be higher than the price', path: ['compareAtPrice'] },
  );
export type ProductInput = z.infer<typeof productSchema>;

export const productImageSchema = z.object({
  url: z.string().trim().min(1).max(500),
  alt: optionalText(200),
  sortOrder: sortOrderSchema,
});

/** Public catalogue query. Every filter is optional; all of them compose. */
export const productListQuerySchema = z.object({
  search: optionalText(120),
  category: optionalText(120),
  brand: optionalText(120),
  minPrice: z.coerce.number().min(0).optional(),
  maxPrice: z.coerce.number().min(0).optional(),
  /** Hide anything with no available stock. The shop's "exclude out of stock" toggle. */
  inStockOnly: z.enum(['true', 'false']).optional().transform((v) => (v === undefined ? undefined : v === 'true')),
  featured: z.enum(['true', 'false']).optional().transform((v) => (v === undefined ? undefined : v === 'true')),
  sort: z.enum(['newest', 'price-asc', 'price-desc', 'name-asc', 'featured']).default('featured'),
});

/** Back-office catalogue query — adds the statuses the public never sees. */
export const adminProductListQuerySchema = productListQuerySchema.extend({
  status: z.enum(PRODUCT_STATUSES).optional(),
  /** At or below the product's reorder level. The Inventory screen's default view. */
  lowStock: z.enum(['true', 'false']).optional().transform((v) => (v === undefined ? undefined : v === 'true')),
});

/* -------------------------------------------------------------------------- */
/* Inventory                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The settings that live alongside the running totals.
 *
 * `onHand` and `reserved` are NOT here, and that is the point: they are the sum of
 * `stock_movements` and are only ever changed by posting one. A screen that could set
 * `on_hand = 40` directly makes the ledger a decoration — the total would no longer
 * mean anything, and the question "where did the other twelve go" would have no
 * answer. Correcting a miscount is an `ADJUSTMENT` movement with a reason. §5.3.
 */
export const inventorySettingsSchema = z
  .object({
    reorderLevel: z.coerce.number().int().min(0).max(1_000_000).default(0),
    location: optionalText(120),
    binCode: optionalText(60),
  })
  .strict();

/**
 * Posting a stock movement — the only way stock changes by hand.
 *
 * `quantity` is unsigned for every type except ADJUSTMENT, whose sign the caller sets
 * because a count can go either way. The direction of all the others is fixed by
 * `MOVEMENT_EFFECT`, so a receipt cannot be booked as a write-off by sending a minus.
 */
export const stockMovementSchema = z
  .object({
    productId: z.coerce.number().int().positive(),
    type: z.enum(MANUAL_MOVEMENT_TYPES),
    quantity: z.coerce.number().int().refine((n) => n !== 0, 'Enter a quantity'),
    /** Supplier invoice, delivery note, stock-count sheet — whatever the paper says. */
    reference: optionalText(120),
    unitCost: priceSchema.optional().nullable(),
    note: optionalText(600),
  })
  .strict()
  .refine((m) => m.type === 'ADJUSTMENT' || m.quantity > 0, {
    message: 'Only a stock adjustment may be negative',
    path: ['quantity'],
  })
  .refine((m) => m.type !== 'ADJUSTMENT' || (m.note != null && m.note.length > 0), {
    message: 'An adjustment needs a reason — it is the only record of why the count changed',
    path: ['note'],
  });
export type StockMovementInput = z.infer<typeof stockMovementSchema>;

export const stockMovementQuerySchema = z.object({
  productId: z.coerce.number().int().positive().optional(),
  type: z.string().trim().max(20).optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
});

/* -------------------------------------------------------------------------- */
/* Cart and checkout                                                           */
/* -------------------------------------------------------------------------- */

/**
 * The cart posted at checkout.
 *
 * It carries product ids and quantities and **no prices**. The client's idea of what
 * something costs is a display detail; the server re-reads every price from the
 * products table before it totals anything. A cart that could name its own prices is
 * a cart the customer can edit in the console. §6 invariant 5.
 */
/**
 * `.strict()` matters here specifically.
 *
 * Without it Zod silently STRIPS unknown keys, so a client posting
 * `{ productId, quantity, unitPrice: '1.00' }` gets a successful order at the real
 * price and no indication its price was discarded. The outcome is safe either way —
 * the server prices from the products table regardless — but silence invites the
 * belief that the field did something. Refusing the request says plainly that a cart
 * carries ids and quantities and nothing else.
 */
export const cartLineSchema = z
  .object({
    productId: z.coerce.number().int().positive(),
    quantity: quantitySchema,
  })
  .strict();

export const checkoutSchema = z
  .object({
    lines: z.array(cartLineSchema).min(1, 'Your cart is empty').max(50),
    customerName: requiredText(160, 'Your name'),
    customerEmail: emailSchema,
    customerPhone: phoneSchema,
    companyName: optionalText(160),
    fulfilment: z.enum(FULFILMENT_METHODS).default('DELIVERY'),
    paymentMethod: z.enum(PAYMENT_METHODS).default('BANK_TRANSFER'),
    addressLine1: optionalText(200),
    addressLine2: optionalText(200),
    city: optionalText(120),
    postalCode: optionalText(20),
    notes: optionalText(2000),
  })
  .strict()
  .refine(
    (o) => o.fulfilment !== 'DELIVERY' || (!!o.addressLine1 && !!o.city),
    { message: 'A delivery address and city are required for delivery', path: ['addressLine1'] },
  );
export type CheckoutInput = z.infer<typeof checkoutSchema>;

/* -------------------------------------------------------------------------- */
/* Orders (back office)                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Advancing an order.
 *
 * The client sends the status it wants; the server checks it against
 * `ORDER_TRANSITIONS` from the order's CURRENT status and refuses anything else. It
 * never trusts a status because the stock side-effects are keyed to the move, not to
 * the destination — arriving at DELIVERED from PENDING would skip the reservation
 * release and leave the ledger permanently short.
 */
export const orderStatusChangeSchema = z
  .object({
    status: z.enum(ORDER_STATUSES),
    note: optionalText(1000),
  })
  .strict();

export const orderUpdateSchema = z
  .object({
    customerName: requiredText(160, 'Customer name'),
    customerEmail: emailSchema,
    customerPhone: phoneSchema,
    companyName: optionalText(160),
    addressLine1: optionalText(200),
    addressLine2: optionalText(200),
    city: optionalText(120),
    postalCode: optionalText(20),
    notes: optionalText(2000),
    internalNotes: optionalText(4000),
  })
  .strict();

export const orderListQuerySchema = z.object({
  search: optionalText(120),
  status: z.enum(ORDER_STATUSES).optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
});

/* -------------------------------------------------------------------------- */
/* Enquiries                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The public enquiry form.
 *
 * `website` is a honeypot: a real browser never fills it because it is hidden, and a
 * bot fills every input it finds. A submission carrying it is accepted with a 200 and
 * quietly dropped — answering "you are a bot" teaches the bot to try again without it.
 */
export const leadSchema = z
  .object({
    name: requiredText(160, 'Your name'),
    email: emailSchema,
    phone: phoneSchema.optional().nullable(),
    company: optionalText(160),
    serviceInterest: optionalText(120),
    budgetRange: optionalText(60),
    message: requiredText(4000, 'A message'),
    source: z.enum(LEAD_SOURCES).default('CONTACT'),
    /** The record this enquiry is about, when it came from a product or project page. */
    referenceType: z.enum(['product', 'project']).optional().nullable(),
    referenceId: z.coerce.number().int().positive().optional().nullable(),
    website: z.string().max(200).optional(),
  })
  .strict();
export type LeadInput = z.infer<typeof leadSchema>;

export const leadUpdateSchema = z
  .object({
    status: z.enum(LEAD_STATUSES),
    assignedToId: z.coerce.number().int().positive().optional().nullable(),
    internalNotes: optionalText(8000),
  })
  .strict();

export const leadListQuerySchema = z.object({
  search: optionalText(120),
  status: z.enum(LEAD_STATUSES).optional(),
  source: z.enum(LEAD_SOURCES).optional(),
});

export const subscribeSchema = z
  .object({
    email: emailSchema,
    website: z.string().max(200).optional(),
  })
  .strict();

/* -------------------------------------------------------------------------- */
/* Audit and history                                                           */
/* -------------------------------------------------------------------------- */

export const auditQuerySchema = z.object({
  search: optionalText(120),
  module: optionalText(60),
  actorId: z.coerce.number().int().positive().optional(),
  method: z.enum(['POST', 'PUT', 'PATCH', 'DELETE']).optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
});

export const revisionQuerySchema = z.object({
  entity: optionalText(60),
  entityId: z.coerce.number().int().positive().optional(),
});

/** Statuses re-exported so a consumer imports one module, not three. */
export const contentStatusValues = CONTENT_STATUSES;
